import type { Signal } from '@preact/signals-react';
import { DelayedRender } from '@visiba/react-std';
import type { ReactNode } from 'react';

type AsyncSignalValueIdle = {
	value: null;
	flowState: 'idle';
};

type AsyncSignalValuePending = {
	value: null;
	flowState: 'pending';
};

type AsyncSignalValueError = {
	value: null;
	flowState: 'error';
};

type AsyncSignalValueAccessible<T> = {
	value: T;
	flowState: 'accessible';
};

export type AsyncSignalValue<T> =
	| AsyncSignalValueIdle
	| AsyncSignalValuePending
	| AsyncSignalValueError
	| AsyncSignalValueAccessible<T>;

interface AsyncSignalProps<T> {
	signal: Signal<AsyncSignalValue<T>>;
	pending?: ReactNode;
	pendingDelay?: number;
	error?: ReactNode;
	children?: ((data: T) => ReactNode) | ReactNode;
}

export function AsyncSignal<T>({ signal, pending, pendingDelay, error, children }: AsyncSignalProps<T>): JSX.Element | null {
	if (signal.value.flowState === 'idle') {
		return null;
	}

	if (signal.value.flowState === 'pending') {
		return pending ? <DelayedRender delay={pendingDelay}>{pending}</DelayedRender> : null;
	}

	if (signal.value.flowState === 'error') {
		return error ? <>{error}</> : null;
	}

	return <>{typeof children === 'function' ? children(signal.value.value) : children}</>;
}

export function isAsyncSignalAccessible<T>(signal: Signal<AsyncSignalValue<T>>): signal is Signal<AsyncSignalValueAccessible<T>> {
	return signal.value.flowState === 'accessible';
}

export function asyncSignalIdle(): AsyncSignalValueIdle {
	return {
		value: null,
		flowState: 'idle',
	};
}

export function asyncSignalPending(): AsyncSignalValuePending {
	return {
		value: null,
		flowState: 'pending',
	};
}

export function asyncSignalError(): AsyncSignalValueError {
	return {
		value: null,
		flowState: 'error',
	};
}

export function asyncSignalAccessible<T>(value: T): AsyncSignalValueAccessible<T> {
	return {
		value: value,
		flowState: 'accessible',
	};
}
